// Branch deep link that opens the mobile app on the login screen with the
// user's email pre-filled. Shared by the redemption success CTA, the
// "magic link" email, and the subscription page download CTA.
//
// The mobile app reads `email` and `navTo` from the Branch link data to
// pre-populate the login field (see urunn lib/services/branch/models/link_data.dart).
// Branch's deferred deep linking carries these params through an App Store
// install, so a brand-new install still lands on login with the email filled.
import branch from 'branch-sdk';

// Universal app link used as a fallback if Branch can't mint a dynamic link
// (e.g. SDK not initialized yet). Resolves to the app / store via AASA.
const FALLBACK_APP_URL = 'https://app.urunn.com';

// Branch Deepview template that controls the web → app open experience for
// these links (configured in the Branch dashboard).
const DEEPVIEW_TEMPLATE = 'urunn_deepview_r6v';

/**
 * Build a Branch magic link that opens the app's login screen with `email`
 * pre-filled. Resolves to a fallback universal link if Branch is unavailable,
 * so callers always get a usable URL.
 */
export function buildAppMagicLink(email?: string | null): Promise<string> {
  return new Promise((resolve) => {
    const linkData = {
      channel: 'web',
      feature: 'app-download',
      campaign: 'web-to-app',
      data: {
        navTo: 'login',
        $deeplink_path: 'login',
        $canonical_identifier: 'app-download/login',
        // Force the configured Branch Deepview template on every platform.
        $ios_deepview: DEEPVIEW_TEMPLATE,
        $android_deepview: DEEPVIEW_TEMPLATE,
        $desktop_deepview: DEEPVIEW_TEMPLATE,
        ...(email ? { email } : {}),
      },
    };

    try {
      branch.link(linkData, (err, link) => {
        if (err || !link) {
          console.warn('[AppLink] branch.link failed, using fallback:', err);
          resolve(FALLBACK_APP_URL);
          return;
        }
        resolve(link);
      });
    } catch (error) {
      console.warn('[AppLink] branch.link threw, using fallback:', error);
      resolve(FALLBACK_APP_URL);
    }
  });
}
